import { BaseAction } from "../../../core/actions/BaseAction";
import { isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { ISaveServiceRequestActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/ServiceRequestActionParams";
import { IAdditionalField } from "../../../Orchestrator/Types/Interface/IServiceRequest";
import { shouldRenderFieldAction } from "./ShouldRenderFieldAction";
import { isEmailField, isValidEmail, isValidMobile, isValidName } from "../../../Lib/Utils/serviceRequestValidation";

class CheckRequiredValidationAction extends BaseAction<IDefaultActionResult, ISaveServiceRequestActionParams> {

    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: false,
            message: "",
            status: "fail"
        }
        try {
            const { additionalFields, formData } = this.params!
            let isValid = false;
            //First filter only required fields
            const requiredFields = additionalFields.filter((f) => f?.required || false);
            if (requiredFields.length > 0) {
                const allRequiredFields: IAdditionalField[] = [];
                for (const field of requiredFields) {
                    if (!field.conditions) {
                        allRequiredFields.push(field);
                    } else {
                        shouldRenderFieldAction.setParams({
                            additionalFields,
                            formData,
                            field
                        });
                        await shouldRenderFieldAction.execute();

                        if (isActionRunSuccessfully(shouldRenderFieldAction)) {
                            const result = shouldRenderFieldAction.result!;
                            if (result.data) {
                                allRequiredFields.push(field);
                            }
                        }
                    }
                }
                if (allRequiredFields.length > 0) {
                    let allFormKeys = Object.keys(formData).filter((key) => {
                        const value = formData[key];
                        return value !== undefined && value !== null && value !== "";
                    });

                    const isAllRequiredFieldCover = allRequiredFields.find(
                        (f) => !allFormKeys.includes(f.fieldId)
                    );
                    if (!isAllRequiredFieldCover) {
                        isValid = true;
                    }
                } else {
                    isValid = true;
                }
            } else {
                isValid = true;
            }

            // Check if this is a Group Booking form
            const selectedCategoryAndSubCategory: any = getRecoilStateValue("selectedCategoryAndSubCategoryAtom");
            const isGroupBookingForm = selectedCategoryAndSubCategory?.category === "GROUP_BOOKING_FORM" && 
                                      selectedCategoryAndSubCategory?.subCategory === "GROUP_BOOKING";

            // Check minimum passenger validation for PASSENGER_TYPE fields (Group Booking Form only)
            if (isValid && isGroupBookingForm) {
                const passengerTypeField = additionalFields.find(
                    (f) => f.type === "PASSENGER_TYPE" && f.min !== undefined && f.min !== null
                );

                if (passengerTypeField) {
                    const minPassenger: any = typeof passengerTypeField.min === "string"
                        ? parseInt(passengerTypeField.min, 10)
                        : passengerTypeField.min;

                    if (!isNaN(minPassenger) && minPassenger > 0) {
                        const passengerValue = formData[passengerTypeField.fieldId];
                        if (passengerValue) {
                            try {
                                const passengers = JSON.parse(passengerValue);
                                const totalWithoutInfants = Object.entries(passengers)
                                    .filter(([passengerType]) => passengerType !== "infants")
                                    .reduce((sum, [, count]) => sum + (count as number), 0);

                                if (totalWithoutInfants < minPassenger) {
                                    isValid = false;
                                }
                            } catch (error) {
                                isValid = false;
                            }
                        } else {
                            isValid = false;
                        }
                    }
                }
            }

            // Check minimum passenger validation for DynamicForm with PROFILE field (not GroupBookingForm)
            if (isValid && !isGroupBookingForm) {
                const passengerTypeField = additionalFields.find(
                    (f) => f.title === "PROFILE"
                );

                if (passengerTypeField) {
                    // Use field.min if provided, otherwise default to 10
                    const minPassenger: number = passengerTypeField.min !== undefined && passengerTypeField.min !== null
                        ? (typeof passengerTypeField.min === "string"
                            ? parseInt(passengerTypeField.min, 10)
                            : passengerTypeField.min as number)
                        : 10;

                    if (!isNaN(minPassenger) && minPassenger > 0) {
                        const passengerValue = formData[passengerTypeField.fieldId];
                        if (passengerValue) {
                            try {
                                const passengers = JSON.parse(passengerValue);
                                const totalWithoutInfants = Object.entries(passengers)
                                    .filter(([passengerType]) => passengerType !== "infants")
                                    .reduce((sum, [, count]) => sum + (count as number), 0);

                                if (totalWithoutInfants < minPassenger) {
                                    isValid = false;
                                }
                            } catch (error) {
                                isValid = false;
                            }
                        } else {
                            isValid = false;
                        }
                    }
                }
            }

            // Check email format validation for fields with tags: ["EMAIL"] or title "EMAIL"
            if (isValid) {
                const emailFields = additionalFields.filter(isEmailField);

                for (const emailField of emailFields) {
                    const emailValue = formData[emailField.fieldId];
                    if (emailValue && emailValue.trim()) {
                        if (!isValidEmail(emailValue)) {
                            isValid = false;
                            break;
                        }
                    }
                }
            }

            // Check name format validation for first name and last name fields (Group Booking Form only)
            if (isValid && isGroupBookingForm) {
                const nameFields = additionalFields.filter((f) => {
                    if (f.type !== "TEXT") return false;
                    const isNameByTitle = f.title?.includes("FIRST_NAME_OF_THE_ORGANISER") || f.title?.includes("LAST_NAME_OF_THE_ORGANISER");
                    const isNameByTags = f.tags && Array.isArray(f.tags) &&
                                       (f.tags.includes("FIRST_NAME_OF_THE_ORGANISER") || f.tags.includes("LAST_NAME_OF_THE_ORGANISER"));
                    return isNameByTitle || isNameByTags;
                });
                
                for (const nameField of nameFields) {
                    const nameValue = formData[nameField.fieldId];
                    if (nameValue && nameValue.trim() && !isValidName(nameValue)) {
                        isValid = false;
                        break;
                    }
                }
            }

            // Check mobile format validation for MOBILE type fields
            if (isValid) {
                const mobileFields = additionalFields.filter(
                    (f) => f.type === "MOBILE"
                );

                for (const mobileField of mobileFields) {
                    const mobileValue = formData[mobileField.fieldId];
                    const countryCode = formData["mobileCountryCode"] || "+971";

                    if (mobileValue && mobileValue.trim()) {
                        if (!isValidMobile(mobileValue, countryCode)) {
                            isValid = false;
                            break;
                        }
                    }
                }
            }

            // Specific demand is optional free text. Required state alone
            // controls whether it must be filled; we don't impose a per-char
            // minimum on what the user typed.

            actionResult.status = "success"
            actionResult.data = isValid

            updateRecoilState("submitRequestButtonDisabledAtom", !isValid)
        } catch (error) {
            actionResult.message = "Something went wrong"
        } finally {
            return actionResult
        }
    }
}

export const checkRequiredValidationAction = new CheckRequiredValidationAction()